import React, { useEffect, useRef, useState } from "react";
import { checkedIcon, keyboard_arrow_down } from "../assets/images/images";
import CloseIcon from "../assets/images/CloseIcon";
import FindCustomer from "./FindCustomer";
import { AccessContext, DEFAULT_ACCESS_CONTEXT, canFindCustomers } from "../services/accessContext";
import { locationKey, selectedLocation } from "../services/currentLocation";

type SwitchOption = { key: string; label: string; selected: boolean };

interface SwitchSelectProps {
  id: string;
  label: string;
  value: string;
  options: SwitchOption[];
  open: boolean;
  onToggle: () => void;
  onSelect: (key: string) => void;
  /** Shown in place of the list when there is nothing to choose from. */
  emptyState?: React.ReactNode;
}

// The list opens INSIDE the popover and is height-capped, so a long one scrolls
// in place instead of running off the screen or under the sidebar's clip.
const SwitchSelect = ({
  id,
  label,
  value,
  options,
  open,
  onToggle,
  onSelect,
  emptyState,
}: SwitchSelectProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Open on the current choice, not at the top of a long list.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open]);

  return (
    <div className="space-y-[6px]">
      <p id={`${id}-label`} className="text-xs text-[#05292A]">
        {label}
      </p>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        aria-labelledby={`${id}-label ${id}-value`}
        onClick={onToggle}
        className="flex items-center justify-between gap-x-2 w-full h-11 lg:h-9 px-3 bg-white border border-[#DBE4E4] rounded-xl text-[13px] lg:text-xs text-[#05292A] text-left"
      >
        <span id={`${id}-value`} className="truncate">
          {value}
        </span>
        <img
          src={keyboard_arrow_down}
          alt=""
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          id={`${id}-panel`}
          ref={listRef}
          role="listbox"
          aria-labelledby={`${id}-label`}
          className="switch-list-scroll max-h-[min(40vh,286px)] bg-white border border-[#DBE4E4] rounded-xl p-1"
        >
          {options.length > 0
            ? options.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={option.selected}
                  onClick={() => onSelect(option.key)}
                  className="flex items-center justify-between gap-x-2 w-full min-h-[44px] lg:min-h-[36px] px-2 rounded-lg text-[13px] lg:text-xs text-left text-[#05292A] hover:bg-[#E7F1F1] active:bg-[#E7F1F1]"
                >
                  <span className="truncate">{option.label}</span>
                  {option.selected && (
                    <img src={checkedIcon} alt="" className="w-3 h-3 shrink-0" />
                  )}
                </button>
              ))
            : emptyState}
        </div>
      )}
    </div>
  );
};

interface SwitchCompanyPopoverProps {
  open: boolean;
  onClose: () => void;
  /** The sidebar's outside-click handler watches this element. */
  popoverRef: React.RefObject<HTMLDivElement>;
  language: any;
  listCompany: any;
  userInfor: any;
  accessContext?: AccessContext;
  onSelectCompany: (companyId: string) => void;
  onSelectLocation: (locationId: string) => void;
  onAddLocation: () => void;
}

/** Company and branch switcher, shared by the desktop sidebar and the mobile drawer. */
const SwitchCompanyPopover = ({
  open,
  onClose,
  popoverRef,
  language,
  listCompany,
  userInfor,
  accessContext = DEFAULT_ACCESS_CONTEXT,
  onSelectCompany,
  onSelectLocation,
  onAddLocation,
}: SwitchCompanyPopoverProps) => {
  const [openList, setOpenList] = useState<null | "company" | "branch">(null);
  const t = (key: string) => language?.[key] ?? key;

  useEffect(() => {
    if (!open) setOpenList(null);
  }, [open]);

  const companies: SwitchOption[] = (listCompany ?? []).map((v: any) => ({
    key: v?.company?.id,
    label: v?.company?.companyName ?? "",
    selected: !!v?.isCurrentActive,
  }));
  const currentBranch = selectedLocation(userInfor?.locations, accessContext);
  const locations: SwitchOption[] = (userInfor?.locations ?? []).map((v: any) => ({
    key: locationKey(v),
    label: v?.name ? v.name : v?.location?.name ?? "",
    selected: !!currentBranch && v === currentBranch,
  }));

  return (
    <div
      ref={popoverRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) onClose();
      }}
      className={`absolute bottom-full left-0 right-0 z-50 mb-2 p-4 space-y-4 rounded-2xl bg-white border border-[#EBF0F0] shadow-[0_12px_32px_rgba(5,41,42,0.16)] max-h-[calc(100dvh-140px)] overflow-y-auto overscroll-contain duration-300 ${
        open
          ? "visible translate-y-0 opacity-100"
          : "invisible pointer-events-none translate-y-2 opacity-0"
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-base font-semibold text-[#05292A]">{t("Switch company")}</p>
        <button
          type="button"
          aria-label={t("Close")}
          className="flex items-center justify-center w-9 h-9 -m-2"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="space-y-3">
        <SwitchSelect
          id="company-switch"
          label={t("Company")}
          value={companies.find((c) => c.selected)?.label || accessContext.currentCompanyName || t("Company")}
          options={companies}
          open={openList === "company"}
          onToggle={() => setOpenList(openList === "company" ? null : "company")}
          onSelect={(key) => {
            setOpenList(null);
            onSelectCompany(key);
          }}
        />
        {open && canFindCustomers(accessContext) && (
          <FindCustomer language={language} onSelect={onSelectCompany} />
        )}
        {accessContext.mode === "support" && (
          <p className="text-[12px] leading-4 text-[#6A7878]">
            {t("Go back to InnovX to find another customer.")}
          </p>
        )}
        <SwitchSelect
          id="branch-switch"
          label={t("Branch")}
          value={locations.find((l) => l.selected)?.label || t("Location")}
          options={locations}
          open={openList === "branch"}
          onToggle={() => setOpenList(openList === "branch" ? null : "branch")}
          onSelect={(key) => {
            setOpenList(null);
            onSelectLocation(key);
          }}
          emptyState={
            <div className="py-3">
              <p className="text-xs text-black text-center">{t("No locations available")}</p>
              <button
                type="button"
                onClick={onAddLocation}
                className="block w-full mt-3 text-xs text-center text-[#078D91]"
              >
                {t("Add as new location?")}
              </button>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default SwitchCompanyPopover;
